import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post, del } from '../lib/api.js';

type BecScenarioType =
  | 'ceo_wire_transfer'
  | 'vendor_changed_banking'
  | 'payroll_redirect'
  | 'gift_card_request'
  | 'invoice_approval'
  | 'credential_request';

type BecOutcome = 'pending' | 'complied' | 'reported' | 'questioned' | 'ignored' | 'forwarded';

interface BecScenarioDefinition {
  key: BecScenarioType;
  name: string;
  description: string;
  spoofed_role: string;
  risk_level: 'critical' | 'high';
  subject_template: string;
  body_preview: string;
  success_condition: string;
  failure_condition: string;
}

interface BecCampaign {
  id: string;
  name: string;
  scenario: BecScenarioType;
  spoofed_sender_name: string;
  spoofed_sender_domain: string;
  target_users: Array<{
    user_id: string;
    user_email: string;
    name?: string;
    department?: string;
    role?: string;
  }>;
  status: 'draft' | 'active' | 'completed';
  company_id?: string;
  started_at?: string;
  completed_at?: string;
  total_targets: number;
  complied_count: number;
  reported_count: number;
  questioned_count: number;
  ignored_count: number;
  forwarded_count: number;
  report_rate?: number;
  compliance_rate?: number;
  created_at: string;
}

interface BecResult {
  id: string;
  campaign_id: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  department?: string;
  role?: string;
  outcome: BecOutcome;
  outcome_details?: string;
  remedial_training_assigned: boolean;
  training_campaign_id?: string;
  recorded_at?: string;
  created_at: string;
}

interface BecAnalytics {
  campaigns_run: number;
  avg_report_rate: number;
  avg_compliance_rate: number;
  best_performing_scenario: string | null;
  department_breakdown: Record<string, { total: number; complied: number; reported: number }>;
}

interface CampaignsResponse {
  data: BecCampaign[];
  hasMore?: boolean;
}

interface ScenariosResponse {
  data: BecScenarioDefinition[];
}

interface ResultsResponse {
  data: BecResult[];
}

interface AnalyticsResponse {
  data: BecAnalytics;
}

function outcomeBadge(outcome: BecOutcome) {
  const config: Record<BecOutcome, { bg: string; text: string; label: string }> = {
    complied: { bg: 'bg-red-100', text: 'text-red-700', label: 'Complied' },
    forwarded: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Forwarded' },
    ignored: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Ignored' },
    questioned: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Questioned' },
    reported: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Reported' },
    pending: { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Pending' },
  };
  const c = config[outcome] ?? config.pending;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function statusBadge(status: string) {
  const colors =
    status === 'active'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'completed'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>
      {status}
    </span>
  );
}

function riskBadge(risk: 'critical' | 'high') {
  return risk === 'critical' ? (
    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
      Critical
    </span>
  ) : (
    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
      High
    </span>
  );
}

export function BecSimulationPage() {
  const { data: campaignsData, loading, error, refresh } = useApi<CampaignsResponse>('/protect/bec');
  const { data: scenariosData } = useApi<ScenariosResponse>('/protect/bec?action=scenarios');
  const { data: analyticsData, refresh: refreshAnalytics } =
    useApi<AnalyticsResponse>('/protect/bec?action=analytics');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bannerCollapsed, setBannerCollapsed] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<BecCampaign | null>(null);
  const [resultsData, setResultsData] = useState<BecResult[] | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formScenario, setFormScenario] = useState<BecScenarioType | ''>('');
  const [formSenderName, setFormSenderName] = useState('');
  const [formSenderDomain, setFormSenderDomain] = useState('');
  const [formTargetEmails, setFormTargetEmails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Record result modal state
  const [recordingResult, setRecordingResult] = useState<BecResult | null>(null);
  const [recordOutcome, setRecordOutcome] = useState<BecOutcome>('reported');
  const [recordDetails, setRecordDetails] = useState('');
  const [recordSubmitting, setRecordSubmitting] = useState(false);

  const campaigns = campaignsData?.data ?? [];
  const scenarios = scenariosData?.data ?? [];
  const analytics = analyticsData?.data;

  const selectedScenarioDef = scenarios.find((s) => s.key === formScenario);

  // Auto-fill sender name from scenario
  function handleScenarioChange(val: BecScenarioType | '') {
    setFormScenario(val);
    if (val) {
      const def = scenarios.find((s) => s.key === val);
      if (def) setFormSenderName(def.spoofed_role);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!formScenario) return;
    setSubmitting(true);
    try {
      const targetUsers = formTargetEmails
        .split(/[\n,]+/)
        .map((e) => e.trim())
        .filter(Boolean)
        .map((email) => ({ user_id: email, user_email: email }));

      await post('/protect/bec', {
        name: formName.trim(),
        scenario: formScenario,
        spoofed_sender_name: formSenderName.trim(),
        spoofed_sender_domain: formSenderDomain.trim(),
        target_users: targetUsers,
      });

      setShowCreateModal(false);
      setFormName('');
      setFormScenario('');
      setFormSenderName('');
      setFormSenderDomain('');
      setFormTargetEmails('');
      refresh();
      refreshAnalytics();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLaunch(id: string) {
    setActionLoading(id);
    try {
      await post(`/protect/bec/${id}?action=launch`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to launch campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleComplete(id: string) {
    setActionLoading(id);
    try {
      await post(`/protect/bec/${id}?action=complete`);
      refresh();
      refreshAnalytics();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this BEC campaign?')) return;
    setActionLoading(id);
    try {
      await del(`/protect/bec/${id}`);
      refresh();
      refreshAnalytics();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function openResults(campaign: BecCampaign) {
    setSelectedCampaign(campaign);
    setResultsData(null);
    setLoadingResults(true);
    try {
      const res = await fetch(
        `/api/protect/bec/${campaign.id}?action=results`,
      ).then((r) => r.json() as Promise<ResultsResponse>);
      setResultsData(res.data ?? []);
    } catch {
      setResultsData([]);
    } finally {
      setLoadingResults(false);
    }
  }

  async function handleRecordResult(e: React.FormEvent) {
    e.preventDefault();
    if (!recordingResult || !selectedCampaign) return;
    setRecordSubmitting(true);
    try {
      await post(`/protect/bec/${selectedCampaign.id}?action=record_result`, {
        user_id: recordingResult.user_id,
        outcome: recordOutcome,
        outcome_details: recordDetails.trim() || undefined,
      });
      setRecordingResult(null);
      setRecordDetails('');
      // Refresh results panel
      await openResults(selectedCampaign);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to record result');
    } finally {
      setRecordSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading BEC campaigns...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">BEC Simulation</h2>
          <p className="text-gray-500 text-sm mt-1">
            Business Email Compromise simulations — test judgment and process, not click behavior.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700"
        >
          + New Campaign
        </button>
      </div>

      {/* Educational Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <span className="text-amber-600 text-lg mt-0.5">&#9888;</span>
            <div>
              <p className="text-sm font-semibold text-amber-800">
                BEC tests judgment, not click behavior.
              </p>
              {!bannerCollapsed && (
                <p className="text-sm text-amber-700 mt-1">
                  These simulations use realistic spoofed emails to test whether users follow
                  proper verification procedures before taking financial or sensitive actions. There
                  are no malicious links — success is measured by what users DO: do they report to
                  IT? Do they follow the fraudulent instructions? Do they forward the email?
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setBannerCollapsed(!bannerCollapsed)}
            className="text-amber-500 hover:text-amber-700 text-xs ml-4 whitespace-nowrap"
          >
            {bannerCollapsed ? 'Show more' : 'Collapse'}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Campaigns Run</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {analytics?.campaigns_run ?? 0}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Avg Report Rate</p>
          <p
            className={`text-2xl font-bold mt-1 ${
              (analytics?.avg_report_rate ?? 0) >= 60
                ? 'text-emerald-600'
                : (analytics?.avg_report_rate ?? 0) >= 30
                  ? 'text-yellow-600'
                  : 'text-red-600'
            }`}
          >
            {analytics?.avg_report_rate ?? 0}%
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Avg Compliance Rate</p>
          <p
            className={`text-2xl font-bold mt-1 ${
              (analytics?.avg_compliance_rate ?? 0) > 10 ? 'text-red-600' : 'text-emerald-600'
            }`}
          >
            {analytics?.avg_compliance_rate ?? 0}%
          </p>
          {(analytics?.avg_compliance_rate ?? 0) > 10 && (
            <p className="text-xs text-red-500 mt-0.5">Above 10% threshold</p>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Best Scenario</p>
          <p className="text-sm font-semibold text-gray-800 mt-2 leading-tight">
            {analytics?.best_performing_scenario ?? '—'}
          </p>
        </div>
      </div>

      {/* Scenario Cards */}
      {scenarios.length > 0 && (
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-3">Available Scenarios</h3>
          <div className="grid grid-cols-2 gap-4">
            {scenarios.map((s) => (
              <div
                key={s.key}
                className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-2"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900">{s.name}</span>
                  {riskBadge(s.risk_level)}
                </div>
                <p className="text-xs text-gray-500">
                  <span className="font-medium text-gray-600">Spoofed role:</span> {s.spoofed_role}
                </p>
                <p className="text-xs text-gray-600 italic">{s.body_preview}</p>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div className="bg-emerald-50 rounded p-2">
                    <p className="text-xs font-medium text-emerald-700 mb-0.5">Pass</p>
                    <p className="text-xs text-emerald-600">{s.success_condition}</p>
                  </div>
                  <div className="bg-red-50 rounded p-2">
                    <p className="text-xs font-medium text-red-700 mb-0.5">Fail</p>
                    <p className="text-xs text-red-600">{s.failure_condition}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFormScenario(s.key);
                    setFormSenderName(s.spoofed_role);
                    setShowCreateModal(true);
                  }}
                  className="mt-1 text-xs text-emerald-600 hover:text-emerald-800 font-medium self-start"
                >
                  Use Scenario &rarr;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaigns Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Campaign</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Scenario</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Targets</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Report %</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Compliance %</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  No BEC campaigns yet. Create one to get started.
                </td>
              </tr>
            ) : (
              campaigns.map((c) => {
                const scenarioDef = scenarios.find((s) => s.key === c.scenario);
                const compRate = c.compliance_rate ?? 0;
                return (
                  <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {scenarioDef?.name ?? c.scenario}
                    </td>
                    <td className="px-4 py-3">{statusBadge(c.status)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{c.total_targets}</td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`font-medium ${
                          (c.report_rate ?? 0) >= 60
                            ? 'text-emerald-600'
                            : (c.report_rate ?? 0) >= 30
                              ? 'text-yellow-600'
                              : 'text-gray-400'
                        }`}
                      >
                        {c.report_rate ?? '—'}
                        {c.report_rate != null ? '%' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`font-medium ${
                          compRate > 10 ? 'text-red-600' : 'text-emerald-600'
                        }`}
                      >
                        {c.compliance_rate ?? '—'}
                        {c.compliance_rate != null ? '%' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openResults(c)}
                        className="text-xs text-blue-600 hover:text-blue-800 mr-2"
                      >
                        Results
                      </button>
                      {c.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleLaunch(c.id)}
                            disabled={actionLoading === c.id}
                            className="text-xs text-emerald-600 hover:text-emerald-800 mr-2 disabled:opacity-50"
                          >
                            Launch
                          </button>
                          <button
                            onClick={() => handleDelete(c.id)}
                            disabled={actionLoading === c.id}
                            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {c.status === 'active' && (
                        <button
                          onClick={() => handleComplete(c.id)}
                          disabled={actionLoading === c.id}
                          className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                        >
                          Complete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">New BEC Campaign</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Q1 Wire Transfer Test"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scenario</label>
                <select
                  value={formScenario}
                  onChange={(e) => handleScenarioChange(e.target.value as BecScenarioType | '')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                >
                  <option value="">Select a scenario...</option>
                  {scenarios.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.name} ({s.risk_level})
                    </option>
                  ))}
                </select>
                {selectedScenarioDef && (
                  <div className="mt-2 bg-gray-50 rounded-md p-3 text-xs text-gray-600 space-y-1">
                    <p className="italic">&ldquo;{selectedScenarioDef.body_preview}&rdquo;</p>
                    <p>
                      <span className="font-medium text-emerald-700">Pass:</span>{' '}
                      {selectedScenarioDef.success_condition}
                    </p>
                    <p>
                      <span className="font-medium text-red-700">Fail:</span>{' '}
                      {selectedScenarioDef.failure_condition}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spoofed Sender Name
                  </label>
                  <input
                    type="text"
                    value={formSenderName}
                    onChange={(e) => setFormSenderName(e.target.value)}
                    placeholder="e.g. CEO"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spoofed Sender Domain
                  </label>
                  <input
                    type="text"
                    value={formSenderDomain}
                    onChange={(e) => setFormSenderDomain(e.target.value)}
                    placeholder="company-ceo.com"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Users (emails, comma or newline separated)
                </label>
                <textarea
                  value={formTargetEmails}
                  onChange={(e) => setFormTargetEmails(e.target.value)}
                  placeholder="alice@company.com&#10;bob@company.com"
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                  required
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create Campaign'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results Slide-Out Panel */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{selectedCampaign.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">Campaign Results</p>
              </div>
              <button
                onClick={() => {
                  setSelectedCampaign(null);
                  setResultsData(null);
                  setRecordingResult(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>

            {/* Summary bar */}
            {!loadingResults && resultsData && resultsData.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Outcome Breakdown
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    [
                      {
                        label: 'Reported',
                        outcome: 'reported',
                        color: 'emerald',
                      },
                      {
                        label: 'Questioned',
                        outcome: 'questioned',
                        color: 'yellow',
                      },
                      {
                        label: 'Ignored',
                        outcome: 'ignored',
                        color: 'gray',
                      },
                      {
                        label: 'Forwarded',
                        outcome: 'forwarded',
                        color: 'orange',
                      },
                      {
                        label: 'Complied',
                        outcome: 'complied',
                        color: 'red',
                      },
                      {
                        label: 'Pending',
                        outcome: 'pending',
                        color: 'slate',
                      },
                    ] as const
                  ).map(({ label, outcome, color }) => {
                    const count = resultsData.filter((r) => r.outcome === outcome).length;
                    const pct =
                      resultsData.length > 0
                        ? Math.round((count / resultsData.length) * 100)
                        : 0;
                    const colorMap: Record<string, string> = {
                      emerald: 'bg-emerald-100 text-emerald-700',
                      yellow: 'bg-yellow-100 text-yellow-700',
                      gray: 'bg-gray-100 text-gray-600',
                      orange: 'bg-orange-100 text-orange-700',
                      red: 'bg-red-100 text-red-700',
                      slate: 'bg-slate-50 text-slate-400',
                    };
                    return (
                      <div
                        key={outcome}
                        className={`rounded-lg p-3 text-center ${colorMap[color]}`}
                      >
                        <p className="text-xl font-bold">{count}</p>
                        <p className="text-xs font-medium">{label}</p>
                        <p className="text-xs opacity-75">{pct}%</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Results table */}
            <div className="flex-1 px-6 py-4">
              {loadingResults ? (
                <p className="text-gray-400 text-sm">Loading results...</p>
              ) : !resultsData || resultsData.length === 0 ? (
                <p className="text-gray-400 text-sm">
                  No results recorded yet.{' '}
                  {selectedCampaign.status === 'active' && 'Launch the campaign first.'}
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left pb-2 font-medium text-gray-600">User</th>
                      <th className="text-left pb-2 font-medium text-gray-600">Role / Dept</th>
                      <th className="text-left pb-2 font-medium text-gray-600">Outcome</th>
                      <th className="text-left pb-2 font-medium text-gray-600">Training</th>
                      <th className="text-right pb-2 font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultsData.map((r) => (
                      <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 pr-2">
                          <p className="font-medium text-gray-900 text-xs">
                            {r.user_name ?? r.user_email}
                          </p>
                          {r.user_name && (
                            <p className="text-xs text-gray-400">{r.user_email}</p>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-xs text-gray-500">
                          {[r.role, r.department].filter(Boolean).join(' / ') || '—'}
                        </td>
                        <td className="py-2 pr-2">{outcomeBadge(r.outcome)}</td>
                        <td className="py-2 pr-2">
                          {r.remedial_training_assigned ? (
                            <span className="text-xs text-amber-700 font-medium">Assigned</span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => {
                              setRecordingResult(r);
                              setRecordOutcome(r.outcome === 'pending' ? 'reported' : r.outcome);
                              setRecordDetails(r.outcome_details ?? '');
                            }}
                            className="text-xs text-emerald-600 hover:text-emerald-800"
                          >
                            Record
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Record Outcome inline form */}
            {recordingResult && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <p className="text-sm font-semibold text-gray-800 mb-3">
                  Record Outcome: {recordingResult.user_name ?? recordingResult.user_email}
                </p>
                <form onSubmit={handleRecordResult} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Outcome</label>
                    <select
                      value={recordOutcome}
                      onChange={(e) => setRecordOutcome(e.target.value as BecOutcome)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="reported">Reported to IT (best)</option>
                      <option value="questioned">Questioned / verified through proper channels</option>
                      <option value="ignored">Ignored / no action</option>
                      <option value="forwarded">Forwarded the email (bad)</option>
                      <option value="complied">Complied with request (worst)</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Notes (optional)
                    </label>
                    <input
                      type="text"
                      value={recordDetails}
                      onChange={(e) => setRecordDetails(e.target.value)}
                      placeholder="What did the user do?"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={recordSubmitting}
                      className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {recordSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecordingResult(null)}
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
